import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import api from "../../api/axios";

/* =========================
   THE SIGN-IN CLIENT
   `api`'s response interceptor turns ANY 401 into localStorage.removeItem +
   window.location.href = "/admin/login" — a full page reload. Node answered a
   wrong password with HTTP 200 and status:401 in the body, so that branch
   never fired on the login screen; the service answers with a real 401, which
   now reloads the page and destroys the rejected-thunk message before it can
   be rendered. The interceptor's one exemption still names /api/adminuser/login,
   a path that no longer exists.

   The four sign-in routes are [no auth]: they want neither the token the
   request interceptor attaches nor the redirect, so they go out on a bare
   client that shares only the base URL.
========================= */
const anon = axios.create({ baseURL: api.defaults.baseURL });

/* =========================
   AUTH HEADER
========================= */
const authHeader = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  },
});

/* =========================
   ERROR TEXT
   The service reports failure with a real HTTP status and a { code, message }
   body — there is no status field inside the body to read any more. Several
   codes carry no message at all (every sign-in one), so their wording lives
   here.
========================= */
const CODE_MESSAGES = {
  missing_credentials:  "Enter your username and password",
  invalid_credentials:  "Invalid username or password",
  sessions_unavailable: "Sign-in is unavailable right now. Please try again in a moment.",
  invalid_challenge:    "That sign-in attempt has expired. Please start again.",
  invalid_code:         "That code is not right. Check your authenticator app.",
  forbidden:            "You are not allowed to do that",
  not_found:            "That account no longer exists",
};

const errorText = (err, fallback) => {
  const data = err.response?.data;
  if (!err.response || !data) return fallback;
  if (data.code === "account_locked") {
    const mins = Number(data.minutes);
    return Number.isFinite(mins)
      ? `Too many attempts. Try again in ${mins} minute${mins === 1 ? "" : "s"}.`
      : "Too many attempts. Please try again later.";
  }
  return data.message || CODE_MESSAGES[data.code] || data.code || fallback;
};

/* What the portal reads back out of localStorage. Shared by the plain login
   and the second-factor one, which mint an identical session. */
const storeSession = (data) => {
  localStorage.setItem("token", data.token);
  localStorage.setItem("adminId", data.user.id);
  localStorage.setItem("username", data.user.username);
  localStorage.setItem("role", data.user.role);  // still 1, 2 or 3
};

/* =========================
   ADMIN LOGIN
========================= */
export const adminLogin = createAsyncThunk(
  "adminUser/login",
  async (payload, thunkAPI) => {
    try {
      const res = await anon.post("/auth/admin/login", payload);

      /* Two 200s carry no token. An account with 2FA on answers
         { requires2fa, challenge, username }; one that has been told to
         enrol and has not answers { requiresEnrolment, challenge, username }.
         Storing res.data.token here would write "undefined" and leave a
         session-less portal, so the ticket is handed straight back for the
         login screen to finish with verifyAdmin2fa / enableAdmin2fa. */
      if (res.data?.requires2fa || res.data?.requiresEnrolment) return res.data;

      storeSession(res.data);
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(errorText(err, "Admin login failed"));
    }
  }
);

/* =========================
   ADMIN LOGIN — SECOND STEP
   The challenge is the ticket from the login response, verbatim; it lives
   five minutes. Only the authenticator's six digits are accepted here —
   backup codes are not.
========================= */
export const verifyAdmin2fa = createAsyncThunk(
  "adminUser/verify2fa",
  async ({ challenge, code }, thunkAPI) => {
    try {
      const res = await anon.post("/auth/admin/2fa/verify", { challenge, code });
      storeSession(res.data);
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(errorText(err, "Two-factor check failed"));
    }
  }
);

/* Enrolment, for the requiresEnrolment branch. setup returns the secret and
   an otpauth URI — there is no server-rendered QR any more, the portal draws
   it — and enable confirms it with a code and hands back ten backup codes,
   shown once. Neither mints a session: after enable the user signs in again
   through adminLogin. */
export const setupAdmin2fa = createAsyncThunk(
  "adminUser/setup2fa",
  async ({ challenge }, thunkAPI) => {
    try {
      const res = await anon.post("/auth/admin/2fa/setup", { challenge });
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(errorText(err, "Could not start two-factor setup"));
    }
  }
);

export const enableAdmin2fa = createAsyncThunk(
  "adminUser/enable2fa",
  async ({ challenge, code }, thunkAPI) => {
    try {
      const res = await anon.post("/auth/admin/2fa/enable", { challenge, code });
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(errorText(err, "Could not turn two-factor on"));
    }
  }
);

/* =========================
   FETCH ADMIN USERS
========================= */
export const fetchAdminUsers = createAsyncThunk(
  "adminUser/fetchAll",
  async (_, thunkAPI) => {
    try {
      /* One of the handlers that still wraps: { message, data: rows }. The
         row is { id, username, role, storeId, phone, isActive, totpEnabled,
         totpRequired, totpEnabledAt, lastLoginAt, lockedUntil } — id, not
         _id, and a number rather than a 24-hex string. */
      const res = await api.get("/admin/accounts", authHeader());
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(errorText(err, "Failed to fetch admin users"));
    }
  }
);

/* =========================
   CREATE ADMIN USER
========================= */
export const createAdminUser = createAsyncThunk(
  "adminUser/create",
  async (payload, thunkAPI) => {
    try {
      // ensure role is sent as number
      const data = { ...payload, role: Number(payload.role) };
      const res = await api.post("/admin/accounts", data, authHeader());
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(errorText(err, "Failed to create admin user"));
    }
  }
);

/* =========================
   UPDATE ADMIN USER
========================= */
export const updateAdminUser = createAsyncThunk(
  "adminUser/update",
  async ({ id, data }, thunkAPI) => {
    try {
      // ensure role is sent as number
      const payload = { ...data, role: Number(data.role) };
      /* The path id is the numeric id off the list row; the route is
         constrained to a long, so a stale 24-hex _id 404s at routing. */
      const res = await api.put(`/admin/accounts/${id}`, payload, authHeader());
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(errorText(err, "Failed to update user"));
    }
  }
);

/* =========================
   DELETE ADMIN USER
========================= */
export const deleteAdminUser = createAsyncThunk(
  "adminUser/delete",
  async (id, thunkAPI) => {
    try {
      await api.delete(`/admin/accounts/${id}`, authHeader());
      return id;
    } catch (err) {
      return thunkAPI.rejectWithValue(errorText(err, "Failed to delete user"));
    }
  }
);

/* =========================
   SLICE
========================= */
const adminUserSlice = createSlice({
  name: "adminUser",
  initialState: {
    list: [],
    token: localStorage.getItem("token") || null,
    role: Number(localStorage.getItem("role")) || null,  // store as number
    /* the ticket a 2FA / enrolment login hands back instead of a session */
    pending2fa: null,
    loading: false,
    success: false,
    error: null,
  },
  reducers: {
    resetStatus: (state) => {
      state.success = false;
      state.error = null;
    },
    adminLogout: (state) => {
      state.token = null;
      state.role = null;
      state.pending2fa = null;
      state.list = [];
      localStorage.clear();
    },
  },
  extraReducers: (builder) => {
    builder
      /* LOGIN */
      .addCase(adminLogin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(adminLogin.fulfilled, (state, action) => {
        state.loading = false;
        /* A second factor is not a session: that branch carries a challenge
           and no token, so no token is stored and the login screen reads
           pending2fa to decide what to ask for next. */
        if (action.payload?.requires2fa || action.payload?.requiresEnrolment) {
          state.pending2fa = {
            mode: action.payload.requires2fa ? "verify" : "enrol",
            challenge: action.payload.challenge,
            username: action.payload.username,
          };
          return;
        }
        state.pending2fa = null;
        state.token = action.payload.token;
        state.role = Number(action.payload.user.role);  // store as number
      })
      .addCase(adminLogin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* SECOND FACTOR */
      .addCase(verifyAdmin2fa.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyAdmin2fa.fulfilled, (state, action) => {
        state.loading = false;
        state.pending2fa = null;
        state.token = action.payload.token;
        state.role = Number(action.payload.user.role);  // store as number
      })
      .addCase(verifyAdmin2fa.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* FETCH */
      .addCase(fetchAdminUsers.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAdminUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload;
      })
      .addCase(fetchAdminUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* CREATE */
      .addCase(createAdminUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAdminUser.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(createAdminUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* UPDATE */
      .addCase(updateAdminUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAdminUser.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(updateAdminUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* DELETE */
      .addCase(deleteAdminUser.fulfilled, (state, action) => {
        state.list = state.list.filter(
          (user) => String(user.id) !== String(action.payload)
        );
      });
  },
});

export const { resetStatus, adminLogout } = adminUserSlice.actions;
export default adminUserSlice.reducer;